import { Label, Radio, RadioGroup, Select, TextField } from '@issuu/ixu-react';
import type { ArticleStorySummary } from 'product-document-page-shared';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import DocumentContext from '../DocumentPage/DocumentContext';

const CenteredLabel = styled(Label).attrs({ layout: 'horizontal-inversed', spacingBottom: 'none' })`
    align-items: center;
    height: 2.2em;
`;

const ShareFromPage = styled.span`
    width: 100%;
    input {
        width: 5em;
        height: 2em;
    }
`;

export type ShareType = 'page' | 'cover' | 'article';
type Props = {
    articleHasPages: ArticleStorySummary[];
    pageCount: number;
    givenPageNumber: number;
    setGivenPageNumber: Dispatch<SetStateAction<number>>;
    shareType: ShareType;
    setShareType: Dispatch<SetStateAction<ShareType>>;
    setShareArticleId: Dispatch<SetStateAction<number | undefined>>;
};

export const SelectShareType: React.FC<Props> = ({
    articleHasPages,
    pageCount,
    givenPageNumber,
    setGivenPageNumber,
    shareType,
    setShareType,
    setShareArticleId,
}: Props) => {
    const {
        appState: { currentPageNumbers },
    } = useContext(DocumentContext);

    const onBlurGivenPageNumber = useCallback(
        (n: string) => {
            if (n === '') {
                setGivenPageNumber(currentPageNumbers[0][0]);
            }
        },
        [givenPageNumber],
    );
    const onChangeGivenPageNumber = useCallback(
        (n: string) => {
            const pageNumber = parseInt(n, 10);
            const testedMin = pageNumber < 1 ? 1 : pageNumber;
            setGivenPageNumber(testedMin > pageCount ? pageCount : testedMin);
            setShareType('page');
        },
        [givenPageNumber],
    );
    const onChangeShareArticle = useCallback((articleId: number) => {
        setShareType('article');
        setShareArticleId(articleId);
    }, []);

    return (
        <RadioGroup onChange={type => setShareType(type as ShareType)} selectedValue={shareType}>
            {articleHasPages.length ? (
                <CenteredLabel>
                    <span>
                        {articleHasPages.length === 1 ? 'Share this story' : 'Share a story'}
                    </span>
                    <Radio value="article" />
                </CenteredLabel>
            ) : null}
            {articleHasPages.length > 1 ? (
                <Select
                    onChange={onChangeShareArticle}
                    optimisticUpdate
                    value={articleHasPages[0].id}
                >
                    {articleHasPages.map(article => (
                        <option key={`share-from-article-${article.id}`} value={article.id}>
                            {article.title}
                        </option>
                    ))}
                </Select>
            ) : null}
            <CenteredLabel>
                <span>Share from cover</span>
                <Radio value="cover" />
            </CenteredLabel>
            <CenteredLabel>
                <ShareFromPage>
                    Share from page
                    <TextField
                        max={pageCount}
                        min={1}
                        onBlur={onBlurGivenPageNumber}
                        onChange={onChangeGivenPageNumber}
                        spacingLeft="small"
                        type="number"
                        value={String(givenPageNumber)}
                    />
                </ShareFromPage>
                <Radio value="page" />
            </CenteredLabel>
        </RadioGroup>
    );
};
